import 'src/hooks/howlerConfig'
import { Howl } from 'howler'

type HowlOptions = ConstructorParameters<typeof Howl>[0]

// The Howl surface this app touches, honoured as no-ops so a dead sound is
// silent instead of a crash. Getters return howler's unloaded-state values.
function createInertHowl(): Howl {
  const inert = {
    play: () => 0,
    stop: () => inert,
    pause: () => inert,
    mute: () => inert,
    volume: () => 0,
    fade: () => inert,
    rate: () => 1,
    seek: () => 0,
    loop: () => false,
    state: () => 'unloaded',
    playing: () => false,
    duration: () => 0,
    on: () => inert,
    off: () => inert,
    once: () => inert,
    load: () => inert,
    unload: () => null
  }
  return inert as unknown as Howl
}

/**
 * Construct a Howl without letting audio init take the page down. Module-scope
 * new Howl() runs during chunk evaluation: a throw there rejects the dynamic
 * import and crashes hydration (seen on Safari — howler's unlock path threw
 * "The provided value is non-finite" and the global error boundary swallowed
 * the whole widget).
 *
 * The retry is deliberate, not hopeful: the only known throwing path is
 * Howler._unlockAudio(), which permanently disables itself (autoUnlock=false)
 * before the code that can throw — so a second construction skips it and
 * succeeds. If audio is broken beyond that, the caller gets an inert Howl and
 * the page lives on without sound.
 */
export function createHowl(options: HowlOptions): Howl {
  try {
    return new Howl(options)
  } catch (err) {
    console.warn('[sound] Howl construction threw — retrying once:', err)
    try {
      return new Howl(options)
    } catch (retryErr) {
      console.warn('[sound] Howl construction failed twice — this sound is disabled:', retryErr)
      return createInertHowl()
    }
  }
}
